import { GetStaticProps } from 'next';

import BlogArticle from '../../../components/blog/BlogArticle';
import { getBlogPost } from '../../../content/blog/posts';
import { DefaultLocale } from '../../../providers/tolgee';

const CHART_ALT =
  'Line chart: the same Texas classes tracked from session 1 to 6, January to June 2026. The share of ECRs scoring 3 or higher out of 5 rises from 40% to 49%, and the share of SCRs earning full credit (2 of 2) rises from 24% to 33%.';

const WhyStaarEcrsScoreZero = () => {
  const post = getBlogPost('why-staar-ecrs-score-zero');
  return (
    <BlogArticle post={post}>
      <p>
        {
          "Why do so many STAAR extended constructed responses (ECRs) score a zero? Read what TEA's rules say about where zeros come from, and what we learned by analyzing 124,000 Texas practice ECRs written in Curipod across 1,400 Texas schools this year."
        }
      </p>
      <h2>{'Four ways to earn a zero'}</h2>
      <ul>
        <li>
          <strong>{'Too short.'}</strong>
          {' Very short responses, eight words or fewer, get an automatic 0.'}
        </li>
        <li>
          <strong>{'Copied.'}</strong>
          {
            ' A response made up mostly of text from the passage or prompt gets an automatic 0.'
          }
        </li>
        <li>
          <strong>{'Off-prompt.'}</strong>
          {' "Responses that do not address the prompt receive a score of zero."'}
        </li>
        <li>
          <strong>{'No claim.'}</strong>
          {
            " A response can be on topic and still score 0 if it has no clear claim or central idea, no structure, and no relevant evidence. TEA's grade 5 examples from 2026 retell the story without taking a position."
          }
        </li>
      </ul>
      <p>
        {
          'In spring 2024, about 1 in 10 STAAR ECRs got an automatic 0, for example for being too short or copied, before a scorer even judged the writing (Cambium Assessment & TEA, 2024).'
        }
      </p>
      <p>
        {
          'A zero also spreads. If a response scores 0 for development and organization, it scores 0 for conventions too (TEA, 2026). So a long, neat response that only retells the text scores the same as a blank page.'
        }
      </p>
      <h2>{'The skill students struggle most with'}</h2>
      <p>
        {
          'When we broke fall practice responses down by skill, one pattern stood out. Students do the first half of a skill and stop.'
        }
      </p>
      <p>
        <strong>
          {
            '64% of responses used text evidence well, but only 41% explained their reasoning.'
          }
        </strong>
        {
          " Students find a good quote and leave it there. The same happens with inference and central idea. Students make a point but don't back it up."
        }
      </p>
      <p>{'This is likely why so many practice ECRs stall at a 3 and few reach a 4.'}</p>
      <h2>{'What practice moves'}</h2>
      <p>
        {
          'Practice helps when classes stick with it. In spring 2026, Curipod followed a fixed group of 190 ECR classes that each ran six or more sessions. '
        }
        <strong>{'Their share of zero scores fell from 18% to 13%,'}</strong>
        {' and more students reached the middle of the rubric.'}
      </p>
      <figure>
        <img src={post.cover} alt={CHART_ALT} />
        <figcaption>
          {
            'After six sessions, the same classes score higher on both short constructed responses (SCRs) and ECRs. Curipod practice data, Texas, January to June 2026.'
          }
        </figcaption>
      </figure>
      <p>
        {
          "The gains weren't only the spring test run-up. In the same month, classes on their fifth session or later scored higher than classes on their first."
        }
      </p>
      <p>
        {
          'By skill, the biggest gains were in explaining reasoning and using evidence, the same skills students struggle with most.'
        }
      </p>
      <table>
        <thead>
          <tr>
            <th>{'Skill'}</th>
            <th>{'Session 1'}</th>
            <th>{'Sessions 3+'}</th>
            <th>{'Change (pts)'}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{'Explains reasoning'}</td>
            <td>{'38%'}</td>
            <td>{'48%'}</td>
            <td>{'+11'}</td>
          </tr>
          <tr>
            <td>{'Uses text evidence'}</td>
            <td>{'61%'}</td>
            <td>{'69%'}</td>
            <td>{'+8'}</td>
          </tr>
          <tr>
            <td>{'Makes an inference'}</td>
            <td>{'61%'}</td>
            <td>{'65%'}</td>
            <td>{'+4'}</td>
          </tr>
          <tr>
            <td>{'Organization'}</td>
            <td>{'69%'}</td>
            <td>{'70%'}</td>
            <td>{'+1'}</td>
          </tr>
          <tr>
            <td>{'Central idea'}</td>
            <td>{'62%'}</td>
            <td>{'63%'}</td>
            <td>{'+1'}</td>
          </tr>
        </tbody>
      </table>
      <p>
        <em>
          {
            'Share of responses passing each skill check in the same classes, first session compared with the third session and later. Curipod practice data, Texas, fall 2026.'
          }
        </em>
      </p>
      <h2>{'Plan for grade 3 and the fall dip'}</h2>
      <p>
        {
          'Grade 3 has the most zeros on both ECRs and SCRs, and the biggest jump in performance comes between grades 3 and 4.'
        }
      </p>
      <p>
        {
          'Fall scores also start lower than spring scores end, likely because students are now reading harder, grade-level texts.'
        }
      </p>
      <p>
        <strong>{'For your planning:'}</strong>
        {
          " don't save ECR practice for spring. Start in the first weeks of the semester, and give grade 3 teachers extra support early."
        }
      </p>
      <h2>{'What HB 8 means for 2027-28'}</h2>
      <p>
        {
          'The SST replaces STAAR in 2027-28. TEA confirms that the end-of-year RLA test "will include a single extended constructed-response item" (TEA, 2025). TEA\'s draft blueprints list the ECR as the only constructed-response item type, and SCRs are not listed (TEA, 2026c). The draft rubric has fewer points, and a response can still score 0 (TEA, 2026b).'
        }
      </p>
      <p>
        <strong>{'For your planning:'}</strong>
        {
          ' the routines you build this year carry forward. With a single ECR on a tighter scale, each zero costs more.'
        }
      </p>
      <h2>{'What to do this semester'}</h2>
      <ol>
        <li>
          <strong>{'See the gaps first.'}</strong>
          {
            ' Use Curipod or another platform that breaks SCR and ECR results down by TEKS reading and writing skill, for each campus, grade and teacher. Look at first attempts, before revision. Add any zero-reason data your testing coordinator can share (TEA, 2024b). Then ask principals and coaches which problem each campus has: too short, copied, no claim, or no explanation.'
          }
        </li>
        <li>
          <strong>
            {'Keep the rhythm simple: one SCR a week, one ECR at the end of each unit.'}
          </strong>
          {
            ' That is five to seven ECRs a year; in the data, gains became clear at about five. Each session runs the same loop: write, get specific feedback, discuss it, revise in the same class period (Hattie & Timperley, 2007; Graham, Harris & Hebert, 2011). A review or intervention block works well, whole class and teacher-led.'
          }
        </li>
        <li>
          <strong>{'Keep teachers in the writing.'}</strong>
          {
            ' Instant feedback helps, and teachers still need to read the writing. Set the expectation that teachers read student responses during and after each session, pick a few to discuss with the class, and talk with students about their writing as they revise. Students write differently when they know their teacher is reading, and teachers see firsthand which skills need reteaching.'
          }
        </li>
      </ol>
      <h2>{'Where Curipod fits'}</h2>
      <p>
        {
          "Curipod is writing practice students love: teacher-led writing practice aligned to your district's curriculum. Students read, write and discuss, not alone on a screen but together. Every student gets specific feedback in the moment, then revises from attempt one to attempt two. It's built on the curriculum you already teach, for all thirty students, and it lets you track writing growth and spot skill gaps across every classroom and school."
        }
      </p>
      <h2>{'A note on the data'}</h2>
      <p>
        {
          'The Curipod figures come from about 124,000 ECRs and more SCRs written in Texas classrooms between January and October 2026. The skill breakdowns and the skill-by-session table cover fall 2026. The other practice findings cover spring 2026. The data is aggregated and de-identified. Curipod does not use student data to train AI models and does not sell or share it.'
        }
      </p>
      <p>
        {
          'Scores come from the feedback in each Curipod lesson, aligned to the STAAR rubrics, and teachers can review and edit that feedback. They are classroom practice scores, not official STAAR results.'
        }
      </p>
      <h2>{'References'}</h2>
      <ul>
        <li>
          {'Cambium Assessment & Texas Education Agency. (2024). '}
          <em>
            {
              'Texas STAAR RLA Spring 2024 Administration: Automated Scoring Methods and Results.'
            }
          </em>{' '}
          <a
            href="https://tea.texas.gov/data-reports/reports-and-studies/2024-staar-rla-hybrid-scoring-report.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            {
              'https://tea.texas.gov/data-reports/reports-and-studies/2024-staar-rla-hybrid-scoring-report.pdf'
            }
          </a>
        </li>
        <li>
          {'Graham, S., Harris, K. R., & Hebert, M. (2011). '}
          <em>{'Informing Writing: The Benefits of Formative Assessment.'}</em>{' '}
          {'Alliance for Excellent Education.'}
        </li>
        <li>
          {'Hattie, J., & Timperley, H. (2007). The power of feedback. '}
          <em>{'Review of Educational Research, 77'}</em>
          {'(1), 81-112.'}
        </li>
        <li>
          {'Texas Education Agency. (2024b). '}
          <em>{'STAAR Hybrid Scoring Key Questions.'}</em>{' '}
          <a
            href="https://tea.texas.gov/data-reports/staar/hybrid-scoring-key-questions-2.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            {
              'https://tea.texas.gov/data-reports/staar/hybrid-scoring-key-questions-2.pdf'
            }
          </a>
        </li>
        <li>
          {'Texas Education Agency. (2025). '}
          <em>{'HB 8 FAQ Update.'}</em>{' '}
          <a
            href="https://tea.texas.gov/data-reports/staar/hb-8-faq-update.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            {'https://tea.texas.gov/data-reports/staar/hb-8-faq-update.pdf'}
          </a>
        </li>
        <li>
          {'Texas Education Agency. (2026). '}
          <em>
            {
              'Grade 5 Reading Language Arts Constructed-Response Scoring Guide, Spring 2026.'
            }
          </em>{' '}
          <a
            href="https://tea.texas.gov/data-reports/staar/released-test-questions/2026-staar-5-rla-scoring-guide.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            {
              'https://tea.texas.gov/data-reports/staar/released-test-questions/2026-staar-5-rla-scoring-guide.pdf'
            }
          </a>
        </li>
        <li>
          {'Texas Education Agency. (2026b). '}
          <em>{'Texas SST Informational Writing Rubric, Grades 3–5 (draft).'}</em>{' '}
          <a
            href="https://tea.texas.gov/data-reports/staar/sst-3-5-informational-writing-rubric.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            {
              'https://tea.texas.gov/data-reports/staar/sst-3-5-informational-writing-rubric.pdf'
            }
          </a>
        </li>
        <li>
          {'Texas Education Agency. (2026c). '}
          <em>{'Draft SST RLA Grade 3 and Grade 8 Summative Blueprints.'}</em>{' '}
          <a
            href="https://tea.texas.gov/data-reports/staar/sst-3-rla-summative-blueprint.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            {'https://tea.texas.gov/data-reports/staar/sst-3-rla-summative-blueprint.pdf'}
          </a>
          {' and '}
          <a
            href="https://tea.texas.gov/data-reports/staar/sst-8-rla-summative-blueprint.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            {'https://tea.texas.gov/data-reports/staar/sst-8-rla-summative-blueprint.pdf'}
          </a>
        </li>
      </ul>
    </BlogArticle>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default WhyStaarEcrsScoreZero;
